/**
 * Winglet API Service
 * Handles all communication with the FastAPI backend
 */

import axios from 'axios';
import { supabase } from '../lib/supabase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';
const API_BASE = import.meta.env.VITE_API_BASE_URL || `${API_URL}/api/v1`;

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ✅ PERFORMANCE FIX: Cache session in memory to avoid repeated async calls
let cachedSession = null;
let sessionCacheTime = 0;
const SESSION_CACHE_DURATION = 5000; // Cache for 5 seconds

// Helper to get cached session or fetch new one
const getSessionToken = async () => {
  const now = Date.now();
  
  // Return cached session if still valid
  if (cachedSession && (now - sessionCacheTime) < SESSION_CACHE_DURATION) {
    return cachedSession?.access_token;
  }
  
  // Fetch new session and cache it
  try {
    const { data: { session } } = await supabase?.auth?.getSession() || {};
    cachedSession = session;
    sessionCacheTime = now;
    return session?.access_token;
  } catch (error) {
    console.error('Failed to get session:', error);
    return null;
  }
};

// Export function to clear cache when user logs out
export const clearSessionCache = () => {
  cachedSession = null;
  sessionCacheTime = 0;
};

// Request interceptor - Add auth token and employee_id if available
apiClient.interceptors.request.use(
  async (config) => {
    // ✅ Use cached session token (much faster!)
    // 🔧 FIX: Don't block requests if auth fails - proceed without token
    try {
      const token = await Promise.race([
        getSessionToken(),
        new Promise((resolve) => setTimeout(() => resolve(null), 1000)) // 1 second timeout
      ]);
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.warn('⚠️ Failed to get auth token, proceeding without auth:', error.message);
      // Continue without token - backend will handle auth check
    }
    
    // Add employee_id as query param if not already present and available
    const employeeId = localStorage.getItem('employeeId');
    if (employeeId && !config.params?.employee_id) {
      config.params = {
        ...config.params,
        employee_id: employeeId
      };
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with error status
      console.error('🚨 API ERROR - Status:', error.response.status);
      console.error('📦 Response Data:', JSON.stringify(error.response.data, null, 2));
      console.error('🔗 Request URL:', error.config?.url);
      console.error('📤 Request Method:', error.config?.method);
      console.error('📋 Request Data:', error.config?.data);
      
      if (error.response.status === 401) {
        // Unauthorized - clear token and redirect to login
        localStorage.removeItem('authToken');
        window.location.href = '/login';
      }
    } else if (error.request) {
      // Request made but no response
      console.error('No response from server:', error.request);
    } else {
      // Error in request setup
      console.error('Request error:', error.message);
    }
    return Promise.reject(error);
  }
);

// ==================== API METHODS ====================

/**
 * Health check - verify API is running
 */
export const healthCheck = async () => {
  try {
    const response = await axios.get(`${API_URL}/health`);
    return response.data;
  } catch (error) {
    console.error('Health check failed:', error);
    return { status: 'error', message: error.message };
  }
};

/**
 * Get API status and database connection info
 */
export const getApiStatus = async () => {
  const response = await apiClient.get('/status');
  return response.data;
};

/**
 * Get current authenticated user
 */
export const getCurrentUser = async () => {
  const response = await apiClient.get('/me');
  return response.data;
};

// ==================== ROTATIONS ====================

/**
 * Get all rotations with optional filters
 */
export const getRotations = async (params = {}) => {
  const response = await apiClient.get('/rotations', { params });
  return response.data;
};

/**
 * Get single rotation by ID
 */
export const getRotationById = async (rotationId) => {
  const response = await apiClient.get(`/rotations/id/${rotationId}`);
  return response.data;
};

/**
 * Get complete rotation data (OPTIMIZED)
 * Single API call with flights, pay, compliance, layovers
 */
export const getCompleteRotation = async (rotationId) => {
  const response = await apiClient.get(`/rotations/id/${rotationId}/complete`);
  return response.data;
};

/**
 * Get compiled rotation data with all calculations
 */
export const getCompiledRotation = async (rotationNumber) => {
  const response = await apiClient.get(`/rotations/${rotationNumber}/compiled`);
  return response.data;
};

/**
 * Create new rotation
 */
export const createRotation = async (rotationData) => {
  const response = await apiClient.post('/rotations', rotationData);
  return response.data;
};

/**
 * Update existing rotation
 */
export const updateRotation = async (rotationId, rotationData) => {
  const response = await apiClient.put(`/rotations/${rotationId}`, rotationData);
  return response.data;
};

/**
 * Delete rotation
 */
export const deleteRotation = async (rotationId) => {
  const response = await apiClient.delete(`/rotations/${rotationId}`);
  return response.data;
};

// ==================== PAY ANALYSIS ====================

/**
 * Get pay analysis for a rotation or date range
 */
export const getPayAnalysis = async (params = {}) => {
  const response = await apiClient.get('/pay/analysis', { params });
  return response.data;
};

/**
 * Run pay calculation for specific rotation
 */
export const calculatePay = async (rotationNumber) => {
  const response = await apiClient.get(`/pay/calculate/rotation/${rotationNumber}`);
  return response.data;
};

/**
 * Get pay calculation for rotation (from rotations endpoint)
 */
export const getRotationPay = async (rotationNumber) => {
  const response = await apiClient.get(`/rotations/${rotationNumber}/pay`);
  return response.data;
};

// ==================== TIMECARDS ====================

/**
 * Get all timecards with optional filters
 */
export const getTimecards = async (params = {}) => {
  const response = await apiClient.get('/timecards', { params });
  return response.data;
};

/**
 * Upload ICS file for timecard processing
 */
export const uploadTimecard = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await apiClient.post('/timecards/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Parse and save rotation text
 */
export const parseRotation = async (rotationText, options = {}) => {
  // Support both old signature (assignmentType string) and new signature (options object)
  const assignmentType = typeof options === 'string' ? options : options.assignment_type || 'Line Holder';
  const isReroute = typeof options === 'string' ? false : options.is_reroute || false;
  const sourceFilename = typeof options === 'string' ? null : options.source_filename || null;
  const uploadClassification = typeof options === 'object' ? options.upload_classification || 'INITIAL_UPLOAD' : 'INITIAL_UPLOAD'; // Phase 1
  
  const response = await apiClient.post('/rotations/parse', {
    text: rotationText,
    assignment_type: assignmentType,
    is_reroute: isReroute,
    source_filename: sourceFilename,
    upload_classification: uploadClassification, // Phase 1
  });
  return response.data;
};

/**
 * Link timecard events to rotation events
 */
export const linkTimecardEvents = async (timecardId, rotationId) => {
  const response = await apiClient.post('/timecards/link', {
    timecard_id: timecardId,
    rotation_id: rotationId,
  });
  return response.data;
};

/**
 * Get compiled timecard with perfect credit hours
 * Auto-compiles on first request if not already compiled
 */
export const getCompiledTimecard = async (timecardId) => {
  const response = await apiClient.get(`/timecards/${timecardId}/compiled`);
  return response.data;
};

/**
 * Compile or recompile timecard
 * @param {number} timecardId - Timecard ID to compile
 * @param {boolean} forceRecompile - Force recompilation even if already compiled
 * @param {string} reason - Reason for compilation
 */
export const compileTimecard = async (timecardId, forceRecompile = false, reason = null) => {
  const response = await apiClient.post(`/timecards/${timecardId}/compile`, {
    force_recompile: forceRecompile,
    compilation_reason: reason
  });
  return response.data;
};

// ==================== CREW VALIDATION ====================

/**
 * Validate crew for a rotation
 */
export const validateCrew = async (rotationId) => {
  const response = await apiClient.post('/crew/validate', { rotation_id: rotationId });
  return response.data;
};

/**
 * Get crew validation history
 */
export const getCrewValidationHistory = async (params = {}) => {
  const response = await apiClient.get('/crew/validation-history', { params });
  return response.data;
};

// ==================== HOTELS ====================

/**
 * Get hotel assignments for rotation
 */
export const getHotels = async (rotationId) => {
  const response = await apiClient.get(`/hotels/rotation/${rotationId}`);
  return response.data;
};

/**
 * Process hotel information for rotation
 */
export const processHotels = async (rotationId) => {
  const response = await apiClient.post('/hotels/process', { rotation_id: rotationId });
  return response.data;
};

// ==================== COMPLIANCE ====================

/**
 * Get compliance violations for rotation
 */
export const getComplianceViolations = async (rotationId, employeeId = '141986') => {
  const response = await apiClient.get(`/compliance/rotation/${rotationId}`, {
    params: { employee_id: employeeId }
  });
  return response.data;
};

/**
 * Run compliance check for a rotation
 */
export const runComplianceCheck = async (rotationId) => {
  const response = await apiClient.get(`/compliance/rotation/${rotationId}`);
  return response.data;
};

// ==================== ANALYTICS ====================

/**
 * Get pay summary statistics
 */
export const getPaySummary = async (params = {}) => {
  const response = await apiClient.get('/analytics/pay-summary', { params });
  return response.data;
};

// ==================== AUDIT LOG ====================

/**
 * Get audit log entries
 */
export const getAuditLog = async (params = {}) => {
  const response = await apiClient.get('/audit/log', { params });
  return response.data;
};

/**
 * Get user settings by employee ID
 */
export const getUserSettings = async (employeeId) => {
  const response = await apiClient.get(`/users/${employeeId}`);
  return response.data;
};

// ==================== EXPORTS ====================

export default {
  healthCheck,
  getApiStatus,
  getRotations,
  getRotationById,
  getCompleteRotation,  // ✅ NEW: Optimized single-call endpoint
  getCompiledRotation,
  createRotation,
  updateRotation,
  deleteRotation,
  getPayAnalysis,
  calculatePay,
  getTimecards,
  uploadTimecard,
  linkTimecardEvents,
  getCompiledTimecard,
  compileTimecard,
  validateCrew,
  getCrewValidationHistory,
  getHotels,
  processHotels,
  getComplianceViolations,
  runComplianceCheck,
  getPaySummary,
  getAuditLog,
  getUserSettings,
};
